import React, { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

const MODES = [
  {
    value: 'overwrite',
    label: 'Overwrite & Reset',
    help: 'Replace my current templates with the latest master copy (Recommended if your manager pushed new scripts).',
  },
  {
    value: 'append',
    label: 'Safe Update (Append Only)',
    help: 'Add new templates without touching my existing custom drips.',
  },
];

/**
 * Warning dialog shown before any re-sync; the agent picks overwrite vs. append.
 * @param {Object} props
 * @param {string} props.sourceName - Manager (or agency default) the templates come from.
 * @param {boolean} props.busy - Re-sync request in flight.
 * @param {() => void} props.onCancel
 * @param {(mode: 'overwrite'|'append') => void} props.onConfirm
 * @returns {JSX.Element}
 */
export default function ResyncConfirmModal({ sourceName, busy = false, onCancel, onConfirm }) {
  const [mode, setMode] = useState('overwrite');
  const destructive = mode === 'overwrite';

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="resync-confirm-modal"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
    >
      <div className="panel w-full max-w-lg p-6 space-y-5 border border-amber-500/40 bg-[#0d0714] rounded-2xl shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
          </div>
          <div className="space-y-1.5">
            <h3 className="text-lg font-bold text-white">⚠️ Re-Sync Account Templates?</h3>
            <p className="text-sm text-white/65 leading-relaxed">
              You have previously customized templates from {sourceName ? <strong className="text-white">{sourceName}</strong> : 'this manager'}.
              A full re-sync will replace your current drips and AI bots with your manager&apos;s newest master settings.
            </p>
          </div>
        </div>

        <fieldset className="space-y-2" disabled={busy}>
          {MODES.map((m) => (
            <label
              key={m.value}
              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                mode === m.value ? 'border-civix-purple bg-civix-purple/10' : 'border-white/10 hover:border-white/25'
              }`}
            >
              <input
                type="radio"
                name="resync-mode"
                value={m.value}
                checked={mode === m.value}
                onChange={() => setMode(m.value)}
                className="mt-1 accent-civix-purple"
              />
              <span>
                <span className="block text-sm font-semibold text-white">{m.label}</span>
                <span className="block text-xs text-white/55 mt-0.5">{m.help}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            data-testid="resync-confirm-btn"
            disabled={busy}
            onClick={() => onConfirm(mode)}
            className={`btn-primary flex items-center gap-2 ${destructive ? 'bg-amber-600 hover:bg-amber-500' : ''}`}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? 'Syncing…' : destructive ? 'Confirm & Overwrite Templates' : 'Confirm Safe Update'}
          </button>
        </div>
      </div>
    </div>
  );
}
